import type { FieldValues, Path, UseFormReturn } from 'react-hook-form';
import { ApiError } from '@opengoal/http';

/**
 * Routes a failed request's field errors back onto the form inputs that caused
 * them, and returns whatever could not be placed.
 *
 * The backend's ValidationPipe reports one message per failed rule, already
 * grouped by property name. Those names are arbitrary strings as far as
 * TypeScript is concerned, while react-hook-form types `setError` against the
 * form's own keys — so anything unrecognised is filtered out here rather than
 * cast away. That is not just a type nicety: a message pinned to a field that
 * does not exist renders nowhere at all, which is worse than a banner.
 *
 * The return value is the leftovers — errors with no matching field (a 409 on a
 * duplicate email, a 403 on seats) — for the caller to show as a banner.
 *
 *   const banner = applyFieldErrors(form, error, ['name', 'seats']);
 */
export function applyFieldErrors<T extends FieldValues>(
  form: UseFormReturn<T>,
  error: unknown,
  fields: readonly Path<T>[],
): string | null {
  if (!(error instanceof ApiError)) return null;

  const { fieldErrors } = error;
  if (!fieldErrors) return error.message;

  const isKnown = (name: string): name is Path<T> => (fields as readonly string[]).includes(name);

  let placed = 0;
  const unplaced: string[] = [];

  for (const [name, messages] of Object.entries(fieldErrors)) {
    if (isKnown(name)) {
      form.setError(name, { type: 'server', message: messages.join(' ') });
      placed += 1;
    } else {
      unplaced.push(...messages);
    }
  }

  if (unplaced.length > 0) return unplaced.join(' ');
  return placed > 0 ? null : error.message;
}
